import React from 'react';
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Button,
  Divider,
  useTheme,
  Grid2
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  BugReport as BugIcon,
  Feedback as FeedbackIcon,
  Support as SupportIcon
} from '@mui/icons-material';

const Support = () => {
  const theme = useTheme();
  
  const faqData = [
    {
      question: "How do I create my first event?",
      answer: "On Home, choose 'Create New Event' and enter an event title, date, and optional description. After creation, Planning Pulse opens with optional setup guidance. Set a budget limit, add tasks, and build your guest list. Completed checklist items disappear, and the checklist goes away when all three steps are done. You can return through 'Planning Pulse' in the event sidebar."
    },
    {
      question: "How do starter templates work?",
      answer: "In Planning Pulse, choose Wedding, Birthday, Baby shower, Corporate event, or General gathering under 'Start with a little inspiration'. Preview the tasks and budget-category percentages, optionally enter a total budget, then apply the template. Each event accepts one template; it adds to existing work without deleting it. Tasks and expense payers start assigned to you. Expense amounts are editable estimates, not payments; without a budget allocation, they start at zero. The inspiration card disappears after applying a template or completing setup. 'Hide inspiration' dismisses it for that event in this browser."
    },
    {
      question: "How are starter-task deadlines chosen?",
      answer: "Deadlines follow your event date, even when the event is years away. Birthdays start about one month before, weddings 6-9 months, baby showers six weeks, corporate events two months, and general gatherings three weeks. For upcoming events, starter deadlines that have already passed become today. You can edit the tasks afterward. Changing the event date does not automatically reschedule existing tasks."
    },
    {
      question: "What does Planning Pulse show?",
      answer: "Planning Pulse highlights overdue tasks and tasks due within seven days, pending RSVPs, missing budget limits, budgets at least 90% allocated or over their limit, unpaid payment milestones due within seven days, and dates needing review. On Home, compact cards show each event's attention count and category summary. Hover, focus with the keyboard, or tap a card to preview up to five items and open that event's full Pulse. Home hides the section when nothing needs attention. Inside an event, Pulse stays visible and paginates longer action lists. Use the refresh icon to load the latest information."
    },
    {
      question: "Can I collaborate with friends on events?",
      answer: "Add a friend through Friends and wait for them to accept the friend request. Then use the 'Share Event' icon on an event card on Home to share that event with them. A friend connection alone does not share your events. Shared organizers can help manage planning information, while applying starter templates, editing public invitation details, and managing private invitation links are owner-only actions. Sharing with an organizer is different from sending a guest an RSVP link."
    },
    {
      question: "How does budget tracking work?",
      answer: "In Budget Tracking, set your event's total budget limit and add expenses with a category and payer. Record advance payments and final payments using the expense controls; progress bars show payment progress. Filter by category or payer. Budget guardrails flag a missing limit, at least 90% allocation, or planned expenses over the limit. These are planning warnings, not a guarantee against overspending. Zero-value starter expenses show 0% paid until you enter amounts."
    },
    {
      question: "What are payment milestones, and do they record payments?",
      answer: "Use the calendar/checklist icon with the 'Payment milestones' tooltip on an expense card to plan a deposit, installment, or final balance. Each milestone has a name, amount, due date, and paid checkbox. You can add up to 20 milestones per expense, and their total cannot exceed that expense. Unpaid milestones due within seven days, including overdue ones, appear in Planning Pulse. Marking a milestone paid does not update 'Paid So Far', advance payments, or actual payments: record those separately in Budget Tracking. Milestones do not process payments or send automatic reminders."
    },
    {
      question: "How do expense splits, reports, and multi-event views work?",
      answer: "Use 'Split Expense' on an expense card to divide an expense equally or enter custom shares. Splits track allocations; they do not transfer money. Open Reports for expense, task, and split summaries. On Home, select multiple event cards and use the multi-event view to review budgets, tasks, guests, calendars, and reports together."
    },
    {
      question: "How do I manage guest RSVPs?",
      answer: "In Guest List Management, add guests or select contacts from your address book. RSVP statuses are 'Pending', 'Attending', and 'Not Attending'. You can edit them as an organizer, or let guests respond through their own private household invitation link. Zero additional guests is valid. The invitation owner chooses the maximum number of additional guests allowed; a guest cannot exceed it, and a declining response must have zero additional guests."
    },
    {
      question: "How do I design and send a private guest invitation?",
      answer: "As the event owner, open Planning Pulse and use the invitation editor at the bottom. Choose Aurora, Sunset, or Garden artwork, enter the location and message you want guests to see, and save the design. Then open Guest List Management and use the send icon with the 'Send invitation' tooltip on a guest card. Set the household's additional-guest allowance and RSVP deadline, create the link, and use the copy or preview icons. Share the copied link yourself by text, email, or another channel. The app does not automatically send guest invitations, and guests do not need an account to RSVP."
    },
    {
      question: "Can I limit how long guests can respond?",
      answer: "Yes. The invitation dialog offers 1 day (the default for new links), 7 days, or a custom RSVP deadline in your local timezone. Deadlines must be in the future and no later than seven days after the event or one year from now, whichever comes first. Use the save icon with the 'Save RSVP deadline' tooltip to change an existing deadline without changing the link or losing its response. Guests can respond or edit until the deadline; after it, the link becomes unavailable and new responses and changes are blocked. Only the owner can change the deadline. Existing links keep their deadline unless the owner updates it."
    },
    {
      question: "How do I know a guest responded, and what do replace and revoke do?",
      answer: "After a household responds through its link, the send icon becomes a green 'Response received' icon. It still opens invitation management. Return to the organizer's browser window or reload the guest list to fetch the latest response. The replace icon invalidates the previous link and requests a fresh response; it does not erase the guest's saved RSVP. The revoke icon makes the link unavailable while preserving the guest's RSVP. When reopening the dialog, the original link cannot be retrieved; replace it if you need a new link to copy."
    },
    {
      question: "What can someone with an invitation link see?",
      answer: "Anyone holding the link can see the event title and date, intended household name, shared location and message, artwork, household guest allowance, current RSVP, and deadline. They can change that household's response until the deadline, so share links privately. Private event descriptions, guest notes, contact details, and other guests are not included. Replace or revoke a link if it reaches the wrong person. Do not include confidential information in the public location or message."
    },
    {
      question: "How do I import or export contacts?",
      answer: "Use 'Import Contacts' and 'Export Contacts' in the event guest page. CSV files need Name, Address, Email, and Phone headers in any order; only Name values are required. Put addresses containing commas or line breaks inside double quotes. Imports accept up to 500 contacts and 50 KB; matching names, emails, or phone numbers are skipped without overwriting existing contacts. Import adds contacts to your address book, not directly to the event; use 'Add From Address Book' afterward. Export downloads all address-book contacts, including those removed from an event, and protects spreadsheet formula-like values as text."
    },
    {
      question: "Why is my event, task, activity, or payment date rejected?",
      answer: "Dates must be real calendar dates between January 1, 2000 and ten years from today. Activities must end after they start; equal start and end times are not accepted. Planning Pulse flags unusual existing event dates and invalid activity ranges for review without automatically changing them. Correct event dates through Planning Pulse and activity times through Calendar. An invitation deadline has its own future-date and lifetime rules."
    },
    {
      question: "Does the app send reminders or work offline?",
      answer: "Planning Pulse and payment milestones provide reminders within the app; they do not send automatic RSVP reminders, payment emails, or push notifications. Share guest invitation links and follow up yourself. An internet connection is needed to load and save data. If loading fails, check your connection and use the refresh icon where available. Do not assume a change was saved unless the app confirms it."
    },
    {
      question: "How do I delete an event?",
      answer: "On Home, use the trash icon with the 'Delete Event' tooltip on your event card, then confirm the deletion. This cannot be undone. Deleting an event removes its associated planning data and private invitation links. Exporting contacts is not a backup of your event's tasks, expenses, or calendar."
    },
  ];

  const supportChannels = [
    {
      title: "Email Support",
      description: "Get help via email - we typically respond within 24 hours",
      icon: <EmailIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      contact: "krishnavn13@outlook.com",
      action: "Send Email",
      emailSubject: "HELP",
      color: 'primary'
    },
    {
      title: "Report a Bug",
      description: "Help us improve by reporting issues or bugs you encounter",
      icon: <BugIcon sx={{ fontSize: 40, color: theme.palette.error.main }} />,
      emailSubject: "BUG REPORT",
      action: "Report Bug",
      color: 'error'
    },
    {
      title: "Feature Request",
      description: "Suggest new features or improvements for the app",
      icon: <FeedbackIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />,
      emailSubject: "FEATURE REQUEST",
      action: "Suggest Feature",
      color: 'success'
    }
  ];

  const handleEmailClick = (subject) => {
    const emailBody = subject === "HELP" 
      ? "Hi,\n\nI need help with the Event Planner app.\n\n[Please describe your issue here]\n\nThank you!"
      : subject === "BUG REPORT"
      ? "Hi,\n\nI found a bug in the Event Planner app.\n\nBug Description:\n[Please describe the bug here]\n\nSteps to reproduce:\n[Please list the steps]\n\nDevice/OS:\n[Please specify your device and OS]\n\nThank you!"
      : "Hi,\n\nI have a feature request for the Event Planner app.\n\nFeature Description:\n[Please describe the feature you'd like to see]\n\nWhy this would be helpful:\n[Please explain why this feature would be useful]\n\nThank you!";
    
    const mailtoLink = `mailto:krishnavn13@outlook.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    window.open(mailtoLink);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
      p: { xs: 1, sm: 2, md: 3 }
    }}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        {/* Header Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 6,
          mt: { xs: 2, sm: 0 }
        }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 2,
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            <SupportIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Support Center
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ 
              mb: 3,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            We're here to help you make your events amazing
          </Typography>
        </Box>

        {/* Support Channels */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ 
            mb: 4, 
            textAlign: 'center',
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}>
            Get Help
          </Typography>
          <Grid2 container spacing={3} justifyContent="center">
            {supportChannels.map((channel, index) => (
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card sx={{
                  height: '100%',
                  textAlign: 'center',
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                      : '0 8px 30px rgba(0, 0, 0, 0.15)',
                  }
                }}>
                  <CardContent sx={{ p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ mb: 3 }}>
                      {channel.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      {channel.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                      {channel.description}
                    </Typography>
                    {channel.contact && (
                      <Typography variant="body2" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                        {channel.contact}
                      </Typography>
                    )}
                    <Button 
                      variant="contained" 
                      color={channel.color}
                      onClick={() => handleEmailClick(channel.emailSubject)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.2,
                        mt: 'auto'
                      }}
                    >
                      {channel.action}
                    </Button>
                  </CardContent>
                </Card>
              </Grid2>
            ))}
          </Grid2>
        </Box>

        <Divider sx={{ 
          my: 6,
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }} />

        {/* FAQ Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ 
            mb: 4,
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}>
            Frequently Asked Questions
          </Typography>
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqData.map((faq, index) => (
              <Accordion 
                key={index} 
                sx={{ 
                  mb: 2,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: '0 0 16px 0',
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  id={`support-faq-${index}`}
                  aria-controls={`support-faq-answer-${index}`}
                  sx={{ 
                    px: { xs: 2, sm: 3 },
                    py: 2
                  }}
                >
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        {/* Quick Tips Section */}
        <Card sx={{
          mb: 6,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 20px rgba(0, 0, 0, 0.2)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(255, 255, 255, 0.2)',
        }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Quick Tips for Better Event Planning
            </Typography>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}>
                  📝 Start Early
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose a starter template suited to your event. Birthday tasks begin about a month before; wedding planning starts 6-9 months ahead. Personalize the dates and estimates.
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.secondary.main }}>
                  👥 Delegate Tasks
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect through Friends, then share the event with an accepted friend. Review task assignees together; guest RSVP links do not grant organizer access.
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.success.main }}>
                  💰 Track Your Budget
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set a realistic limit, review allocation warnings, and plan deposits with payment milestones. Record actual payments separately so your paid totals stay accurate.
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.warning.main }}>
                  📱 Stay Updated
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review Planning Pulse and set household RSVP deadlines. Copy private invitation links and follow up yourself; automatic guest reminders are not sent.
                </Typography>
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            © {new Date().getFullYear()} Event Planner. All rights reserved.
          </Typography>
          <Button
            href="/privacy"
            variant="text"
            color="primary"
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Privacy Policy
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Support; 